import type { StudyPlan } from 'modules/studyPlan';
import type { IeltsSkillKey } from 'modules/result/types';

/** The few figures the IELTS page needs, taken from the learner's results and study plan. */
export interface IeltsResultLike {
  status: 'completed' | 'in_progress' | 'abandoned';
  completedAt: Date | string | null;
  examScore: { standard: 'IELTS' | 'SAT'; value: number | null } | null;
  examSkills?: { scope: IeltsSkillKey | null; bands: Partial<Record<IeltsSkillKey, number>> } | null;
}

export interface IeltsSummary {
  /** Overall bands of finished full attempts, oldest first. */
  bands: number[];
  attempts: number;
  best: number | null;
  lastAt: Date | null;
  target: number | null;
}

const MAX_TREND_POINTS = 6;

export function summarizeIelts(results: readonly IeltsResultLike[], targetLevel: string | null | undefined): IeltsSummary {
  const ielts = results.filter((r) => r.examScore?.standard === 'IELTS' && r.status !== 'abandoned');
  const finished = ielts
    .filter((r) => r.status === 'completed' && typeof r.examScore?.value === 'number' && r.completedAt)
    .sort((a, b) => new Date(a.completedAt as Date).getTime() - new Date(b.completedAt as Date).getTime());
  const bands = finished.map((r) => r.examScore!.value as number);
  const parsed = Number.parseFloat(targetLevel ?? '');
  const last = finished[finished.length - 1];
  return {
    bands: bands.slice(-MAX_TREND_POINTS),
    attempts: ielts.length,
    best: bands.length ? Math.max(...bands) : null,
    lastAt: last?.completedAt ? new Date(last.completedAt) : null,
    target: Number.isFinite(parsed) && parsed >= 4.5 && parsed <= 9 ? parsed : null,
  };
}

export interface WeakTopic {
  topic: string;
  mastery: number;
}

/** The weakest topics with enough answers to trust, lowest mastery first. */
export function weakestTopics(mastery: StudyPlan['mastery'] | undefined, limit = 3): WeakTopic[] {
  return (mastery?.groups ?? [])
    .flatMap((group) => group.topics)
    .filter((topic) => !topic.lowData)
    .sort((a, b) => a.mastery - b.mastery)
    .slice(0, limit)
    .map(({ topic, mastery: value }) => ({ topic, mastery: value }));
}

export const IELTS_SKILLS: readonly IeltsSkillKey[] = ['listening', 'reading', 'writing', 'speaking'];

export interface SkillProgress {
  skill: IeltsSkillKey;
  /** Finished attempts that cover this skill: a full mock counts for every skill. */
  attempts: number;
  best: number | null;
}

/** Per skill: how many finished attempts covered it and the best band earned in it. */
export function summarizeSkills(results: readonly IeltsResultLike[]): SkillProgress[] {
  return IELTS_SKILLS.map((skill) => {
    const covering = results.filter(
      (r) => r.status === 'completed' && r.examSkills && (r.examSkills.scope === skill || r.examSkills.scope === null),
    );
    const bands = covering.map((r) => r.examSkills!.bands[skill]).filter((band): band is number => typeof band === 'number');
    return { skill, attempts: covering.length, best: bands.length ? Math.max(...bands) : null };
  });
}
